import React, { useEffect, useState, useRef, useId } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon, CheckIcon } from 'lucide-react';
export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}
export interface SelectProps {
  options: SelectOption[];
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  error?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  className?: string;
}
export function Select({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  label,
  error,
  disabled = false,
  fullWidth = true,
  className = ''
}: SelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const id = useId();
  const selectedOption = options.find(opt => opt.value === value);
  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (isOpen && highlightedIndex >= 0) {
          const option = options[highlightedIndex];
          if (!option.disabled) {
            onChange(option.value);
            setIsOpen(false);
          }
        } else {
          setIsOpen(true);
        }
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setHighlightedIndex(prev => prev < options.length - 1 ? prev + 1 : prev);
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (isOpen) {
          setHighlightedIndex(prev => prev > 0 ? prev - 1 : prev);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
    }
  };
  return <div ref={containerRef} className={`relative ${fullWidth ? 'w-full' : ''} ${className}`} data-id="element-516">
      {label && <label htmlFor={id} className="block text-body-sm font-medium text-surface-700 mb-1.5" data-id="element-517">
          {label}
        </label>}
      <button id={id} type="button" onClick={() => !disabled && setIsOpen(!isOpen)} onKeyDown={handleKeyDown} disabled={disabled} aria-haspopup="listbox" aria-expanded={isOpen} aria-labelledby={label ? `${id}-label` : undefined} className={`
          w-full flex items-center justify-between
          px-4 py-2.5 text-body-md text-left
          bg-white border rounded-input
          transition-colors duration-150
          ${error ? 'border-error-500 focus:ring-2 focus:ring-error-100' : 'border-surface-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-100'}
          ${disabled ? 'bg-surface-100 text-surface-500 cursor-not-allowed' : ''}
        `} data-id="element-518">
        <span className={selectedOption ? 'text-surface-900' : 'text-surface-400'} data-id="element-519">
          {selectedOption?.label || placeholder}
        </span>
        <ChevronDownIcon className={`w-5 h-5 text-surface-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-520" />
      </button>

      <AnimatePresence data-id="element-521">
        {isOpen && <motion.ul ref={listRef} initial={{
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={{
        duration: 0.15
      }} role="listbox" aria-labelledby={label ? `${id}-label` : undefined} className="
              absolute z-50 w-full mt-1
              bg-white border border-surface-200 rounded-card shadow-elevated
              max-h-60 overflow-auto
              py-1
            " data-id="element-522">
            {options.map((option, index) => <li key={option.value} role="option" aria-selected={option.value === value} aria-disabled={option.disabled} onClick={() => {
          if (!option.disabled) {
            onChange(option.value);
            setIsOpen(false);
          }
        }} onMouseEnter={() => setHighlightedIndex(index)} className={`
                  flex items-center justify-between
                  px-4 py-2.5 text-body-md cursor-pointer
                  ${option.disabled ? 'text-surface-400 cursor-not-allowed' : ''}
                  ${highlightedIndex === index && !option.disabled ? 'bg-primary-50 text-primary-700' : ''}
                  ${option.value === value ? 'text-primary-600 font-medium' : 'text-surface-700'}
                `} data-id="element-523">
                {option.label}
                {option.value === value && <CheckIcon className="w-4 h-4 text-primary-500" data-id="element-524" />}
              </li>)}
          </motion.ul>}
      </AnimatePresence>

      {error && <p className="mt-1.5 text-body-sm text-error-500" role="alert" data-id="element-525">
          {error}
        </p>}
    </div>;
}